import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { UserCheck, Mail, ShieldCheck, Check } from "lucide-react";

const STEP_IMAGES = ["/images/cf/step1.png", "/images/cf/step2.png", "/images/cf/step3.png"];

export default function HowItWorksSteps() {
  const { t } = useLanguage();

  const STEPS = [
    {
      num: "1",
      SideIcon: UserCheck,
      label: t("cf.s1label"),
      titleParts: [t("cf.s1t1"), t("cf.s1t2")],
      desc: t("cf.s1desc"),
      bullet: t("cf.s1bullet"),
      img: STEP_IMAGES[0],
    },
    {
      num: "2",
      SideIcon: Mail,
      label: t("cf.s2label"),
      titleParts: [t("cf.s2t1"), t("cf.s2t2")],
      desc: t("cf.s2desc"),
      bullet: t("cf.s2bullet"),
      img: STEP_IMAGES[1],
    },
    {
      num: "3",
      SideIcon: ShieldCheck,
      label: t("cf.s3label"),
      titleParts: [t("cf.s3t1"), t("cf.s3t2")],
      desc: t("cf.s3desc"),
      bullet: t("cf.s3bullet"),
      img: STEP_IMAGES[2],
    },
  ];

  return (
    <div className="relative">
      <div className="absolute left-[18px] top-8 bottom-8 border-l-2 border-dashed border-[#D4A85D]/30 hidden md:block" />

      <ol className="space-y-7">
        {STEPS.map((s) => (
          <li key={s.num} className="relative grid md:grid-cols-[40px_1fr] gap-4" data-testid={`step-${s.num}`}>
            <div className="w-9 h-9 rounded-full bg-[#071A35] text-white font-bold flex items-center justify-center flex-shrink-0 z-10">
              {s.num}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-[#071A35]/10 rounded-2xl p-5 lg:p-6 shadow-md grid lg:grid-cols-[260px_1fr_60px] gap-5 items-center"
            >
              <div className="rounded-xl overflow-hidden bg-[#F8F4EB] h-44 flex items-center justify-center">
                <img
                  src={s.img}
                  alt={s.label}
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              </div>

              <div>
                <p className="text-[#D4A85D] text-xs uppercase tracking-[0.2em] font-bold mb-2">{s.label}</p>
                <h3
                  className="text-2xl text-[#071A35] mb-3 leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {s.titleParts[0]}<span className="italic text-[#D4A85D]">{s.titleParts[1]}</span>
                </h3>
                <p className="text-[#071A35]/75 text-sm leading-relaxed mb-3">{s.desc}</p>
                <div className="flex items-start gap-2 text-[#071A35]/85 text-sm">
                  <div className="w-5 h-5 rounded-full border border-[#D4A85D] bg-[#D4A85D]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#D4A85D]" strokeWidth={3} />
                  </div>
                  <span>{s.bullet}</span>
                </div>
              </div>

              <div className="hidden lg:flex items-center justify-center">
                <div className="w-14 h-14 rounded-full border border-[#D4A85D]/40 bg-[#D4A85D]/10 flex items-center justify-center">
                  <s.SideIcon className="w-6 h-6 text-[#D4A85D]" strokeWidth={1.5} />
                </div>
              </div>
            </motion.div>
          </li>
        ))}
      </ol>
    </div>
  );
}
